import { TableDataNumberFilter, TableDataNumberFilterType } from '../services/api/table.service';

const NUMBER_FILTER_TYPES: ReadonlySet<TableDataNumberFilterType> = new Set([
  'equals',
  'notEqual',
  'lessThan',
  'lessThanOrEqual',
  'greaterThan',
  'greaterThanOrEqual',
  'inRange',
  'blank',
  'notBlank',
]);

// Backend contract is single-condition; combined AND/OR models are dropped on purpose.
export function extractMeasureFilter(filterModel: unknown): TableDataNumberFilter | undefined {
  const model = filterModel as Record<string, unknown> | undefined | null;
  if (!model) return undefined;

  for (const [field, raw] of Object.entries(model)) {
    if (field === 'timestamp') continue;
    const condition = raw as { filterType?: string; type?: string; filter?: unknown; filterTo?: unknown } | null | undefined;
    if (condition?.filterType !== 'number' || !condition.type) continue;
    if (!NUMBER_FILTER_TYPES.has(condition.type as TableDataNumberFilterType)) continue;

    const value = typeof condition.filter === 'number' ? condition.filter : null;
    const valueTo = typeof condition.filterTo === 'number' ? condition.filterTo : null;
    return {
      field,
      type: condition.type as TableDataNumberFilterType,
      value,
      valueTo,
    };
  }
  return undefined;
}

// Automatic-measure and analysis-post follow-ups use the shared date-range
// filter on `timestamp` and agSetColumnFilter on `status` alongside the
// existing number columns. Returns the first non-trivial column filter or
// `undefined` to mean "no filter", honouring the same single-condition wire
// contract as ``extractMeasureFilter``.
export function extractAutomaticMeasureFilter(filterModel: unknown): TableDataNumberFilter | undefined {
  const model = filterModel as Record<string, unknown> | undefined | null;
  if (!model) return undefined;

  for (const [field, raw] of Object.entries(model)) {
    if (raw == null) continue;

    if (field === 'timestamp') {
      const dateFilter = extractDateRangeFilter(field, raw);
      if (dateFilter) return dateFilter;
      continue;
    }

    const condition = raw as Record<string, unknown>;

    if (field === 'status') {
      const setFilter = extractSetFilter(field, condition);
      if (setFilter) return setFilter;
    }

    const numberFilter = extractNumberFilter(field, condition);
    if (numberFilter) return numberFilter;
  }
  return undefined;
}

// The shared date-range filter models `[from, to]` local-day bounds; either side
// may be open. The backend widens `to` into a half-open day window `[from, to+1d)`.
function extractDateRangeFilter(field: string, model: unknown): TableDataNumberFilter | undefined {
  if (!Array.isArray(model)) return undefined;
  const value = model[0] instanceof Date ? model[0].toISOString() : null;
  const valueTo = model.length >= 2 && model[1] instanceof Date ? model[1].toISOString() : null;
  if (!value && !valueTo) return undefined;
  return { field, type: 'dateRange', value, valueTo };
}

function extractSetFilter(field: string, condition: Record<string, unknown>): TableDataNumberFilter | undefined {
  if (condition['filterType'] !== 'set' || !Array.isArray(condition['values'])) return undefined;
  const values = (condition['values'] as unknown[])
    .map(v => (typeof v === 'number' ? v : typeof v === 'string' ? Number.parseInt(v, 10) : Number.NaN))
    .filter((v: number) => Number.isFinite(v));
  if (values.length === 0) return undefined;
  return { field, type: 'set', value: null, valueTo: null, values };
}

function extractNumberFilter(field: string, condition: Record<string, unknown>): TableDataNumberFilter | undefined {
  if (condition['filterType'] !== 'number' || typeof condition['type'] !== 'string') return undefined;
  const op = condition['type'] as TableDataNumberFilterType;
  if (!NUMBER_FILTER_TYPES.has(op)) return undefined;
  const value = typeof condition['filter'] === 'number' ? condition['filter'] : null;
  const valueTo = typeof condition['filterTo'] === 'number' ? condition['filterTo'] : null;
  return { field, type: op, value, valueTo };
}
